import parseJwt from "@helpers/parseJwt"

import wait from "@helpers/wait"
import { HOST, isBackstage as _isBackstage } from "@components/constants"

import request, { apiRequest } from "./request"
import {
  ERROR_UNAUTHORIZED,
  getAuthentication,
  isPendingAuthentication
} from "./authenticate"

const AUTHORIZE_USER_URL = `${HOST}/api/backstage/Authorize`
const AUTHORIZE_INVESTOR_URL = `${HOST}/api/investments/Authorize`
const AUTHORIZE_ROOT_USER_URL = `${HOST}/api/backstage/AuthorizeAdministrator`

const ERROR_ACCESS_DENIED = "AccessDeniedError"


export const commonRequest = async (
  onUnexpectedError,
  operation,
  parameters,
  expectedErrors = [],
  headers = {}
) => {
  let result

  try {
    result = await apiRequest(operation, parameters, headers)

  } catch (error) {
    error.isExpected = expectedErrors.includes(error.originalError?.code)

    if (!error.isExpected) {
      onUnexpectedError(error)
    }

    throw error
  }

  return result
}


export const authorizedRequest = (onUnexpectedError, operation, parameters, expectedErrors) =>
  commonRequest(onUnexpectedError, operation, parameters, expectedErrors, { authorization: window.authorization })


export const authenticatedRequest = (onUnexpectedError, operation, parameters, expectedErrors, isBackstage) =>
  commonRequest(onUnexpectedError, operation, parameters, expectedErrors, { authentication: getAuthentication(isBackstage) })


const waitForAuthentication = async () => {
  do {
    await wait(50)
  } while (isPendingAuthentication())

  return getAuthentication(_isBackstage)
}


const authorize = (
  setIdentity,
  onAuthorizationError,
  authorizationMethod,
  authentication,
  organizationId,
  commitmentId
) =>
  authorizationMethod(authentication, organizationId, commitmentId)
    .then(([ authorization, identity ]) => {
      window.authorization = authorization
      window.authorizationClaims = parseJwt(authorization)

      setIdentity(identity)
      return identity
    })
    .catch(error => {
      const isAuthenticationExpired = error.code === ERROR_UNAUTHORIZED
      const isAccessDenied = error.code === ERROR_ACCESS_DENIED

      if (isAuthenticationExpired) {
        const shouldAuthenticate = !isPendingAuthentication()

        if (shouldAuthenticate) {
          onAuthorizationError(error)
        }

        return waitForAuthentication()
          .then(newAuthentication =>
            authorize(
              setIdentity,
              onAuthorizationError,
              authorizationMethod,
              newAuthentication,
              organizationId,
              commitmentId
            )
          )
      }

      onAuthorizationError(error)

      if (isAccessDenied) {
        return
      }

      throw error
    })


export const authorizeInvestor = async (authentication, organizationId, commitmentId) => {
  const query = {}

  if (commitmentId) {
    query.commitmentId = commitmentId
  }

  const { data } = await request(AUTHORIZE_INVESTOR_URL, query, { authentication })
  const { authorizationJwt, ...investor } = data

  return [ authorizationJwt, investor ]
}


export const authorizeUser = async (authentication, organizationId) => {
  const parameters = {}

  if (organizationId) {
    parameters.organizationId = organizationId
  }

  const headers = { authentication }
  const { isRoot } = parseJwt(authentication)

  const shouldAuthorizeAsRoot = isRoot && organizationId
  const url = shouldAuthorizeAsRoot ? AUTHORIZE_ROOT_USER_URL : AUTHORIZE_USER_URL

  const { data } = await request(url, parameters, headers)
  const { authorizationJwt, ...user } = data

  return [ authorizationJwt, user ]
}

export default authorize
